import { useEffect, useState } from 'react';
import AdminShiftsPanel from './components/AdminShiftsPanel';
import BonusRulesPanel from './components/BonusRulesPanel';
import CashJournalPanel from './components/CashJournalPanel';
import ChefPanelNew from './components/ChefPanelNew';
import Dashboard from './components/Dashboard';
import EmployeeCalendarPanel from './components/EmployeeCalendarPanel';
import EmployeeProfile from './components/EmployeeProfile';
import EmployeesPanel from './components/EmployeesPanel';
import Layout from './components/Layout';
import LoginScreen from './components/LoginScreen';
import MenuPanel from './components/MenuPanel';
import PayrollPanelNew from './components/PayrollPanelNew';
import SettingsPanel from './components/SettingsPanel';
import ShiftTypesPanel from './components/ShiftTypesPanel';
import SouvenirsPanel from './components/SouvenirsPanel';
import StatisticsPanel from './components/StatisticsPanel';
import TelegramPanel from './components/TelegramPanel';
import TelephonyPanel from './components/TelephonyPanel';
import VisitsPanel from './components/VisitsPanel';
import WhatsAppPanel from './components/WhatsAppPanel';
import YandexTicketsPanel from './components/YandexTicketsPanel';

const sectionRoles = {
    dashboard: ['owner', 'admin', 'manager', 'staff', 'viewer'],
    visits: ['owner', 'admin', 'manager', 'viewer'],
    statistics: ['owner', 'admin', 'manager'],
    'cash-journal': ['owner', 'admin', 'manager'],
    settings: ['owner', 'admin', 'manager'],
    menu: ['owner', 'admin', 'manager'],
    souvenirs: ['owner', 'admin', 'manager'],
    chef: ['owner', 'admin', 'chef'],
    profile: ['staff'],
    employees: ['owner', 'manager'],
    'shift-types': ['owner'],
    'bonus-rules': ['owner'],
    payroll: ['owner'],
    'admin-shifts': ['owner', 'admin', 'manager'],
    'employee-calendar': ['owner', 'admin', 'manager'],
    'yandex-tickets': ['owner', 'viewer'],
    whatsapp: ['owner', 'admin', 'manager'],
    telegram: ['owner', 'admin', 'manager'],
    telephony: ['owner', 'admin']
};

const HIDDEN_SECTIONS = ['chef', 'whatsapp', 'telegram', 'telephony'];

const getFirstAllowedSection = (role) => Object.entries(sectionRoles)
    .find(([id, roles]) => !HIDDEN_SECTIONS.includes(id) && roles.includes(role))?.[0] || 'dashboard';

function App() {
    const [activeSection, setActiveSection] = useState('dashboard');
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        try {
            const savedToken = localStorage.getItem('token');
            const savedUser = localStorage.getItem('user');

            if (savedToken && savedUser) {
                setToken(savedToken);
                setUser(JSON.parse(savedUser));
            }
        } catch (error) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
        }
        setIsLoading(false);
    }, []);

    useEffect(() => {
        if (!user) {
            return;
        }

        const allowedRoles = sectionRoles[activeSection] || [];
        if (HIDDEN_SECTIONS.includes(activeSection) || !allowedRoles.includes(user.role)) {
            setActiveSection(getFirstAllowedSection(user.role));
        }
    }, [activeSection, user]);

    const handleLoginSuccess = (userData, userToken) => {
        setUser(userData);
        setToken(userToken);
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        setToken(null);
        setActiveSection('dashboard');
    };

    if (isLoading) {
        return <div>Загрузка...</div>;
    }

    // Если пользователь не авторизован, показываем экран входа
    if (!user || !token) {
        return <LoginScreen onLoginSuccess={handleLoginSuccess} />;
    }

    const renderSection = () => {
        switch (activeSection) {
            case 'dashboard':
                return <Dashboard user={user} token={token} />;
            case 'visits':
                return <VisitsPanel user={user} token={token} />;
            case 'statistics':
                return <StatisticsPanel user={user} token={token} />;
            case 'cash-journal':
                return <CashJournalPanel user={user} token={token} />;
            case 'settings':
                return <SettingsPanel user={user} token={token} />;
            case 'menu':
                return <MenuPanel user={user} token={token} />;
            case 'souvenirs':
                return <SouvenirsPanel token={token} />;
            case 'chef':
                return <ChefPanelNew user={user} token={token} />;
            case 'yandex-tickets':
                return <YandexTicketsPanel user={user} token={token} />;
            case 'whatsapp':
                return <WhatsAppPanel user={user} token={token} />;
            case 'telegram':
                return <TelegramPanel />;
            case 'telephony':
                return <TelephonyPanel />;
            case 'employees':
                return <EmployeesPanel user={user} token={token} />;
            case 'shift-types':
                return <ShiftTypesPanel user={user} token={token} />;
            case 'bonus-rules':
                return <BonusRulesPanel user={user} token={token} />;
            case 'payroll':
                return <PayrollPanelNew user={user} token={token} />;
            case 'admin-shifts':
                return <AdminShiftsPanel user={user} token={token} />;
            case 'employee-calendar':
                return <EmployeeCalendarPanel user={user} token={token} />;
            case 'profile':
                return <EmployeeProfile user={user} token={token} />;
            default:
                return <Dashboard />;
        }
    };

    return (
        <Layout
            activeSection={activeSection}
            onSectionChange={setActiveSection}
            user={user}
            onLogout={handleLogout}
        >
            {renderSection()}
        </Layout>
    );
}

export default App;
